// ============================================================
// detectorHealth — "esta frota está com detector passando fome?"
// ============================================================
// PROBLEMA QUE RESOLVE:
//   A varredura de detectores do Client SDK é SERIAL numa thread só. Um
//   detector que bloqueia segura TODOS os outros — inclusive os de cadência de
//   1 s. Numa sessão real medimos um tick de 262,9 s (ProcessScanner, primeiro
//   tick com cache de arquivo frio) e o anti-cheat ficou cego por 4 min e meio.
//   O SDK ganhou contadores pra isso, mas eles morriam no log local — e o build
//   de RELEASE não grava log em disco desde o hardening de 2026-06-30.
//
//   Agora os contadores chegam no painel. Este arquivo é o que impede que eles
//   cheguem como quatro colunas de números que ninguém sabe ler.
//
// ⛔ ISTO NÃO É SEVERIDADE DE EVENTO, E NÃO PODE VIRAR.
//   `TomEvento` (lib/eventHumanizer.ts) é um juízo sobre A CONDUTA DO JOGADOR,
//   travado contra o motor de risco pelo contrato em
//   tests/eventHumanizer.contract.test.ts. O vocabulário DESTE arquivo é
//   deliberadamente separado e deliberadamente disjunto: aqui o sujeito é
//   sempre um DETECTOR NOSSO, nunca uma pessoa.
//
//   Por isso não existe `critico`, não existe `forte`, e nenhum rótulo daqui
//   coincide com `rotuloDoTom`. Um detector lento é problema NOSSO; a máquina
//   lenta é do jogador. Se um dia alguém quiser reaproveitar `TomEvento` aqui
//   "pra ficar consistente", o efeito prático seria pintar de vermelho-acusação
//   a máquina de quem tem HD mecânico — e o passo seguinte, inevitável, seria
//   alguém somar isso a um score. O teste em tests/detectorHealth.test.ts trava
//   a disjunção.
//
//   O que É reaproveitado, de propósito, é a convenção VISUAL: cinza para "não
//   é sobre o jogador" (mesmo cinza de `corDoTom('operacional')`), e uma rampa
//   de gravidade legível na mesma linguagem do resto do painel. Sistema
//   quebrado precisa PARECER quebrado; o que não pode é PARECER acusação — e
//   isso quem carrega são as palavras, que aqui falam só de detector.
// ============================================================

import type { FleetDetectorHealth, FleetSchedulerHealth } from '../services/schedulerHealth';

/**
 * Gravidade de UMA linha de detector. Mede tempo de CEGUEIRA da frota, não
 * conduta de ninguém.
 */
export type SaudeDetector =
  | 'ok'        // rodou dentro da cadência configurada
  | 'atrasado'  // saiu da cadência, mas em ordem de grandeza tolerável
  | 'faminto';  // ficou sem rodar muito além da cadência — anti-cheat cego

/**
 * Papel do detector no episódio. É a distinção que os quatro contadores crus
 * NÃO dão sozinhos, e sem a qual o operador lê a tabela ao contrário: numa
 * varredura serial, o detector com o PIOR atraso é quase sempre a VÍTIMA, e o
 * culpado é outro — o que teve o tick longo. Ranquear só por atraso põe cinco
 * vítimas no topo e esconde a única linha acionável.
 */
export type PapelDetector =
  | 'nenhum'
  | 'segurou_fila'   // teve tick longo: travou a thread e parou os outros
  | 'ficou_parado'   // venceu enquanto outro segurava a thread
  | 'ambos';

/**
 * Tempo que a frota passou sem este detector, no pior caso da janela.
 *
 * Unifica os dois lados do mesmo fenômeno: `worst_lateness_ms` é quanto ESTE
 * detector ficou sem rodar; `worst_run_ms` é quanto ele segurou a thread — e
 * durante um tick longo o anti-cheat também está cego, só que para os outros
 * quinze. Somar seria errado (é o mesmo intervalo de tempo); o máximo é a
 * leitura honesta.
 */
export function cegueiraMs(d: FleetDetectorHealth): number {
  return Math.max(d.worst_lateness_ms ?? 0, d.worst_run_ms ?? 0);
}

/**
 * `faminto` a partir de 30 s absolutos OU do veredito de razão vindo do SDK.
 *
 * ⚠️ 2026-08-06 (tarde) — `cadenciaMs()` FOI REMOVIDA daqui, e a metade "10× a
 * cadência" do critério mudou de lado. Não é simplificação: `cadence_ms` saiu
 * do wire (é o mapa operacional da nossa detecção), e sem ela esta função
 * assumiria o piso de 1 s para TODO detector — 10× viraria 10 s absolutos, e um
 * detector de cadência 5 min atrasado 12 s passaria a ser rotulado "passando
 * fome". Falso alarme, e SILENCIOSO: não haveria 400, não haveria log, o rótulo
 * simplesmente mudaria e alguém seria acordado de madrugada por causa dele.
 *
 * Quem conhece a cadência é o SDK, então é ele quem aplica a razão e manda só o
 * VEREDITO (`sessions_starved` = em quantas sessões ele se declarou faminto).
 * A constante do lado de lá é `kStarvationCadenceRatio`, em
 * client-sdk/src/profiling/SchedulerHealthPayload.h.
 *
 * OS DOIS CRITÉRIOS CONTINUAM EXISTINDO, e continuam existindo pelo mesmo
 * motivo de sempre: cadência sozinha engana nas duas pontas (10× de um detector
 * de 5 min seriam 50 min de cegueira chamados de "atraso"), e 30 s fixos num
 * detector de 1 s deixariam passar 29 s de cegueira. Vale o que estourar
 * primeiro. O que mudou é ONDE cada um roda.
 *
 * ⚠️ CUSTO DECLARADO: mudar o 30 s continua sendo uma constante aqui; mudar o
 * 10× passou a exigir release de DLL. Foi a fronteira escolhida — só o critério
 * que PRECISA da cadência atravessou.
 *
 * `sessions_starved` ausente (payload de backend antigo, ou sessão que só tem
 * SDK legado) degrada para o piso de 30 s, que é conservador na direção certa:
 * subestima gravidade em vez de inventar alarme.
 */
const CEGUEIRA_ABSOLUTA_FAMINTO_MS = 30_000;

/**
 * ⚠️ 2026-08-06 (manhã) — A FRASE AFIRMAVA UMA CHECAGEM QUE NÃO ACONTECEU.
 *
 * O texto do ramo "atrasados" dizia, literalmente:
 *   "Nenhum ficou sem rodar por 10× a cadência nem por 30 s."
 *
 * A metade do 30 s a tela DE FATO conferiu — `cegueiraMs` de toda linha ficou
 * abaixo do piso, senão o ramo seria o de fome. A metade do 10× ela NÃO
 * conferiu, e não tem como conferir: o critério de razão vive no SDK (só ele
 * conhece a cadência) e chega aqui exclusivamente como o veredito agregado em
 * `sessions_starved`. Num SDK legado esse veredito NUNCA é emitido — e o
 * backend grava `starved` com o default `false`
 * (DiagnosticsService.cpp, `jd["starved"] = d.starved`, mais o
 * `COALESCE(d.starved, false)` do SQL de frota). Numa frota 100% legada a
 * afirmação era, portanto, sobre uma checagem que não rodou.
 *
 * POR QUE A RESSALVA É INCONDICIONAL, e não um `if`:
 *   Não existe sinal, nesta tela, que separe "o SDK avaliou a razão e ninguém
 *   passou fome" de "o SDK nem sabe o que é razão". Os dois produzem
 *   `sessions_starved: 0`, byte por byte — a informação é destruída no backend,
 *   no default acima. E a única prova POSITIVA possível (`sessions_starved > 0`
 *   em alguma linha) é inalcançável NESTE ramo por construção: uma linha assim
 *   é classificada `faminto` por `classificarSinais`, e o ramo de fome retorna
 *   antes. Um `if` aqui teria, portanto, um lado morto e um lado que continuaria
 *   chutando — que é o defeito com mais uma camada de tinta.
 *
 * O CONSERTO CERTO, se um dia se quiser a frase forte de volta, é do outro lado:
 * o backend contar quantos snapshots da janela trazem a CHAVE `starved` (não o
 * valor), e publicar esse número. Aí a tela poderia afirmar a razão quando o
 * número bate com `snapshots_reporting`, e só aí. Enquanto esse campo não
 * existir, a tela diz o que mediu e nomeia o que não mediu.
 */
const RESSALVA_CRITERIO_DE_RAZAO =
  'O critério de 10× a cadência é aplicado pelo SDK — esta tela não conhece '
  + 'cadência e só recebe o veredito pronto —, e SDK legado não o emite: nesta '
  + 'janela ele pode simplesmente não ter sido avaliado.';

/**
 * Sinais crus de UM detector, na forma mínima que a classificação precisa.
 *
 * ⚠️ 2026-08-06 (noite) — ISTO NASCEU DE UM DEFEITO, não de gosto por
 * abstração. A MESMA verdade era classificada em DOIS lugares: aqui, para a
 * tabela de FROTA, e inline no JSX da tabela POR SESSÃO
 * (pages/Diagnostics/Diagnostics.tsx). A cópia de lá não tinha ramo `ok` — o
 * ternário só escolhia entre `faminto` e `atrasado`. No formato delta isso
 * estava certo (só sobe quem teve problema); num roster LEGADO, que manda os 17
 * detectores com zeros incluídos, as 15 linhas saudáveis eram pintadas como
 * "fora da cadência". Uma sessão perfeita era apresentada como 15 detectores
 * com problema — e o operador tem exatamente uma reação a isso: parar de
 * confiar na coluna.
 *
 * Duas classificações da mesma verdade em arquivos diferentes é COMO elas
 * divergem. Agora é uma só, e as duas telas chamam a mesma função.
 */
export interface SinaisDoDetector {
  late_runs?: number;
  slow_runs?: number;
  /**
   * Veredito de fome do PRÓPRIO SDK (ele conhece a cadência configurada; esta
   * tela não — ver o bloco acima). Ausente em payload legado.
   */
  starved?: boolean;
  /** Pior tempo de cegueira em ms — já unificado pelo MÁXIMO, nunca pela soma. */
  cegueira_ms: number;
}

/**
 * A classificação, em UM lugar só.
 *
 * O ramo `ok` NÃO é decorativo e NÃO é inalcançável: ele é o único desfecho
 * correto para linha de roster LEGADO (zeros incluídos), que é o que a frota
 * mista do rollout manda. Quem sumir com ele reintroduz o defeito descrito em
 * `SinaisDoDetector`.
 *
 * `starved` entra também no guarda do `ok`: um detector que o SDK declarou
 * faminto nunca pode cair em "dentro da cadência" só porque os contadores
 * daquele campo vieram zerados.
 */
export function classificarSinais(s: SinaisDoDetector): SaudeDetector {
  const late = s.late_runs ?? 0;
  const slow = s.slow_runs ?? 0;
  const famintoPeloSdk = s.starved === true;

  if (late === 0 && slow === 0 && !famintoPeloSdk) return 'ok';

  if (s.cegueira_ms >= CEGUEIRA_ABSOLUTA_FAMINTO_MS) return 'faminto';
  if (famintoPeloSdk) return 'faminto';
  return 'atrasado';
}

export function classificarDetector(d: FleetDetectorHealth): SaudeDetector {
  return classificarSinais({
    late_runs: d.late_runs,
    slow_runs: d.slow_runs,
    // Na frota o veredito do SDK chega AGREGADO: "em quantas sessões ele se
    // declarou faminto". Uma basta.
    starved: (d.sessions_starved ?? 0) > 0,
    cegueira_ms: cegueiraMs(d),
  });
}

/**
 * Mesma classificação, aplicada a UMA linha da tabela POR SESSÃO.
 *
 * Os nomes dos campos diferem do agregado de frota (`max_lateness_ms` /
 * `max_run_ms` contra `worst_lateness_ms` / `worst_run_ms`) porque são
 * contratos diferentes: um é o bloco persistido daquela sessão, o outro é o
 * agregado da janela. O CRITÉRIO é o mesmo, e passou a ser literalmente o
 * mesmo código.
 */
export function classificarDetectorDaSessao(d: {
  late_runs?: number;
  slow_runs?: number;
  max_lateness_ms?: number;
  max_run_ms?: number;
  starved?: boolean;
}): SaudeDetector {
  return classificarSinais({
    late_runs: d.late_runs,
    slow_runs: d.slow_runs,
    starved: d.starved,
    cegueira_ms: Math.max(d.max_lateness_ms ?? 0, d.max_run_ms ?? 0),
  });
}

export function papelDetector(d: FleetDetectorHealth): PapelDetector {
  const segurou = (d.slow_runs ?? 0) > 0;
  const parado = (d.late_runs ?? 0) > 0;
  if (segurou && parado) return 'ambos';
  if (segurou) return 'segurou_fila';
  if (parado) return 'ficou_parado';
  return 'nenhum';
}

/**
 * Classes Tailwind por saúde — mesma GRAMÁTICA de `corDoTom` (texto + borda +
 * fundo translúcido, mesmo formato de badge), mas uma PALETA própria.
 *
 * `corDoTom` já ocupa vermelho (`critico`), laranja (`forte`), amarelo
 * (`contexto`) e azul-céu (`info`) para a escala de acusação — e `critico` é a
 * ÚNICA cor daquela escala que promete "isto encerra sessão sozinho". Se
 * `faminto` (o pior estado DESTA escala) usasse a mesma cor, o operador teria
 * como distinguir os dois só pelo texto — e texto é a última coisa que se lê
 * numa tabela, cor é a primeira. Foi exatamente essa colisão (as duas escalas
 * produzindo `text-red-300 border-red-500/40 bg-red-500/10`) que motivou esta
 * revisão: "nossa varredura travou" e "jogador alterou o binário" não podem
 * chegar ao olho com a mesma cor.
 *
 * Por isso `atrasado`/`faminto` usam ÍNDIGO — cor que `corDoTom` não usa em
 * lugar nenhum, e que o resto do painel também não usa para severidade
 * (aparece em `AttackCategoryBadge`/`Events` como rótulo neutro de categoria,
 * nunca como "isto é grave"). Um só matiz nas duas intensidades dá à saúde de
 * agendamento uma identidade PRÓPRIA e reconhecível — não "mais um vermelho
 * fraco perto do vermelho de verdade".
 *
 * `ok` continua cinza, DE PROPÓSITO: é o único par que pode coincidir com
 * `corDoTom('operacional')`, porque os dois dizem a mesma coisa ("isto não é
 * sobre o jogador"). O contrato de cor em
 * tests/eventHumanizer.contract.test.ts trava exatamente essa exceção e
 * nenhuma outra.
 */
export function corDaSaude(s: SaudeDetector): string {
  switch (s) {
    case 'faminto':
      return 'text-indigo-300 border-indigo-500/40 bg-indigo-500/10';
    case 'atrasado':
      return 'text-indigo-200 border-indigo-400/30 bg-indigo-400/10';
    case 'ok':
      // Mesmo cinza de `corDoTom('operacional')`: "não é sobre o jogador".
      return 'text-gray-400 border-gray-600/40 bg-gray-600/10';
  }
}

/**
 * Rótulo curto da saúde.
 *
 * Nenhum destes pode coincidir com `rotuloDoTom` — ver o bloco ⛔ no topo e o
 * teste de disjunção. "passando fome" é literalmente a pergunta que o operador
 * faz; responder com a mesma palavra é o que torna a tela legível sem legenda.
 */
export function rotuloDaSaude(s: SaudeDetector): string {
  switch (s) {
    case 'faminto':
      return 'passando fome';
    case 'atrasado':
      return 'fora da cadência';
    case 'ok':
      return 'dentro da cadência';
  }
}

/** Rótulo do papel — diz se a linha é causa ou consequência. */
export function rotuloDoPapel(p: PapelDetector): string {
  switch (p) {
    case 'segurou_fila':
      return 'segurou a fila';
    case 'ficou_parado':
      return 'ficou parado na fila';
    case 'ambos':
      return 'segurou a fila e ficou parado';
    case 'nenhum':
      return '—';
  }
}

/**
 * Duração legível. 262900 ms vira "4 min 23 s", e não "262900" — o número cru
 * em milissegundos foi exatamente o que fez 4 minutos de cegueira passarem
 * despercebidos por semanas.
 */
export function formatarDuracao(ms: number | undefined | null): string {
  if (ms === undefined || ms === null || Number.isNaN(ms)) return '—';
  if (ms <= 0) return '0';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) {
    const s = ms / 1000;
    return `${s.toFixed(1).replace('.', ',')} s`;
  }
  const totalS = Math.round(ms / 1000);
  const min = Math.floor(totalS / 60);
  const s = totalS % 60;
  if (min < 60) return s === 0 ? `${min} min` : `${min} min ${s} s`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** Linha da tabela, já classificada e pronta pra render. */
export interface LinhaDetector {
  dado: FleetDetectorHealth;
  saude: SaudeDetector;
  papel: PapelDetector;
  cegueiraMs: number;
}

/**
 * Ordena por tempo de cegueira decrescente.
 *
 * Poderia ordenar por `late_runs` (frequência) — descartado: numa frota grande
 * um detector que atrasa 200 ms mil vezes somaria mais "eventos" que o
 * ProcessScanner de 262,9 s, e o incidente que motivou tudo isto apareceria no
 * fim da lista. Frequência de atraso pequeno é ruído de escalonamento; o que
 * importa é o maior buraco de detecção.
 */
export function ordenarDetectores(dets: FleetDetectorHealth[]): LinhaDetector[] {
  return dets
    .map((d) => ({
      dado: d,
      saude: classificarDetector(d),
      papel: papelDetector(d),
      cegueiraMs: cegueiraMs(d),
    }))
    .sort((a, b) => b.cegueiraMs - a.cegueiraMs
      || a.dado.detector_name.localeCompare(b.dado.detector_name));
}

/** O que a tela precisa dizer em UMA frase, antes de qualquer tabela. */
export type EstadoFrota =
  | 'sem_snapshot'      // ninguém mandou perf-snapshot na janela
  | 'ninguem_reporta'   // mandaram, mas nenhum SDK reporta escalonamento
  | 'sem_atribuicao'    // reportam totais do registry, sem dizer de qual detector
  | 'saudavel'
  | 'degradada'
  /**
   * A resposta chegou, mas um dos campos que o veredito PRECISA pra decidir
   * entre "saudável" e "problema" veio ausente ou não é número. NÃO é o mesmo
   * que `sem_snapshot` (aquele é "sabemos que não chegou nada"; este é "não
   * sabemos o que chegou"). Existe para que um backend antigo, um proxy que
   * trunca campo, ou uma resposta parcial durante rollout nunca sejam lidos
   * como "tudo bem" só porque `undefined === 0` avalia falso em todo guarda.
   */
  | 'dados_incompletos';

export interface VeredictoFrota {
  estado: EstadoFrota;
  frase: string;
  /** Explica o que fazer / por que o dado está assim. */
  detalhe: string;
  saude: SaudeDetector;
  /**
   * Rótulo do badge PARA ESTE VEREDITO — não confundir com `rotuloDaSaude`,
   * que é para LINHAS de detector individuais (onde `saude` sempre reflete um
   * número real, porque a linha só existe se um detector real chegou nela).
   * Aqui `saude` às vezes vale `'ok'` só pelo cinza ("isto não é uma
   * acusação"), e reusar `rotuloDaSaude('ok')` → "dentro da cadência" faria o
   * badge dos estados sem_snapshot/ninguem_reporta/dados_incompletos AFIRMAR
   * saúde que não temos como afirmar. `rotulo` é escolhido por estado, não
   * derivado da cor.
   */
  rotulo: string;
  famintos: number;
  atrasados: number;
}

/**
 * Lê um campo numérico sem confundir "ausente" com "zero".
 *
 * `undefined === 0` é `false` — comparar um campo direto com `=== 0` faz um
 * payload que OMITE o campo (SDK velho, backend truncado, resposta parcial)
 * tomar o mesmo caminho de código que um payload que manda `0` de propósito.
 * São dois fatos diferentes: um é "sabemos, e está zerado"; o outro é "não
 * sabemos nada". Esta função é o que permite ao veredito nunca mais confundir
 * os dois — foi confundir exatamente isso que fez ausência de dado virar
 * "Nenhum detector saiu da cadência nesta janela".
 */
function numeroOuIndefinido(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

/** Veredito para o terceiro estado de verdade: nem saudável, nem problema — não sabemos. */
function veredictoDadosIncompletos(campo: string, janela: string): VeredictoFrota {
  return {
    estado: 'dados_incompletos',
    frase: 'Não sabemos se a saúde do escalonamento desta frota está boa.',
    detalhe:
      `A resposta da janela de ${janela} veio sem o campo \`${campo}\` (ou com um `
      + 'valor que não é número). Isto não é "nenhum detector atrasou" — é '
      + 'ausência de dado, provavelmente um backend ou proxy desatualizado na '
      + 'frente desta chamada. Tratar ausência como saúde repetiria o mesmo '
      + 'erro que deixou 4 min e meio de anti-cheat cego passarem despercebidos.',
    saude: 'ok',
    rotulo: 'não sabemos',
    famintos: 0,
    atrasados: 0,
  };
}

/**
 * O veredito é a única coisa que o operador PRECISA ler. A tabela existe para
 * quem já leu o veredito e quer saber de quem.
 *
 * Os cinco estados são distintos de propósito — em especial `ninguem_reporta` e
 * `saudavel`. Colapsá-los (tabela vazia = "tudo certo") seria a forma mais fácil
 * de reintroduzir a cegueira que este trabalho existe pra acabar: frota muda
 * pareceria frota sadia, exatamente como já parecia antes.
 *
 * ⚠️ 2026-08-06 (tarde) — POR QUE NÃO NASCEU UM SEXTO ESTADO.
 * Com o formato delta, "ninguém atrasou" também chega com a tabela vazia, e a
 * pergunta óbvia foi se isso exigia um estado novo. Não exige, e a razão é que
 * o conserto foi feito UM NÍVEL ABAIXO: `snapshots_attributed` passou a contar
 * CAPACIDADE ("este SDK sabe dizer de quem") em vez de presença de linhas. Com
 * isso, os três ramos abaixo continuam significando o que sempre significaram:
 *   - `attributed === 0`   → nenhum SDK sabe atribuir  → `sem_atribuicao`;
 *   - `attributed  >  0` e nada com problema → `saudavel`, tabela vazia OU não;
 *   - `reporting === 0`    → ninguém mandou o bloco    → `ninguem_reporta`.
 * Um sexto estado ("delta vazio") teria sido um remendo em cima de um campo que
 * mentia — e mentiria também para o SQL de frota, que lê o mesmo `attributed`
 * em outros dois lugares. O teste-âncora dos três casos está em
 * tests/detectorHealth.test.ts.
 */
export function veredicto(f: FleetSchedulerHealth | null): VeredictoFrota {
  if (!f) {
    return {
      estado: 'sem_snapshot',
      frase: 'Nenhum snapshot de performance chegou nesta janela.',
      detalhe:
        'Sem snapshot não há como afirmar nada sobre o escalonamento — nem que '
        + 'está bem, nem que está mal. Se a frota deveria estar enviando, o '
        + 'problema é anterior a esta tela.',
      saude: 'ok',
      rotulo: 'sem dado',
      famintos: 0,
      atrasados: 0,
    };
  }

  // `window_minutes` alimenta toda mensagem abaixo — se vier ausente, mostrar
  // "undefined min" seria o mesmo defeito desta rodada num lugar diferente.
  const janelaMin = numeroOuIndefinido(f.window_minutes);
  const janela = janelaMin !== undefined ? `${janelaMin} min` : 'janela desconhecida';

  // ------------------------------------------------------------
  // Três guardas, três campos essenciais. Cada um precisa distinguir "veio
  // zero" (fato) de "não veio" (ausência) — ver `numeroOuIndefinido`. Isto é
  // o conserto do defeito ⚠️ CAMPO AUSENTE VIRA ATESTADO DE SAÚDE: antes,
  // comparar direto com `=== 0` fazia `undefined === 0` (falso) atravessar os
  // três guardas e cair no ramo "saudável" com "undefined" nos números.
  // ------------------------------------------------------------
  const snapshots = numeroOuIndefinido(f.snapshots);
  if (snapshots === undefined) return veredictoDadosIncompletos('snapshots', janela);

  if (snapshots === 0) {
    return {
      estado: 'sem_snapshot',
      frase: 'Nenhum snapshot de performance chegou nesta janela.',
      detalhe:
        'Sem snapshot não há como afirmar nada sobre o escalonamento — nem que '
        + 'está bem, nem que está mal. Se a frota deveria estar enviando, o '
        + 'problema é anterior a esta tela.',
      saude: 'ok',
      rotulo: 'sem dado',
      famintos: 0,
      atrasados: 0,
    };
  }

  const reporting = numeroOuIndefinido(f.snapshots_reporting);
  if (reporting === undefined) return veredictoDadosIncompletos('snapshots_reporting', janela);

  if (reporting === 0) {
    return {
      estado: 'ninguem_reporta',
      frase: 'Nenhum SDK desta frota reporta saúde de escalonamento.',
      detalhe:
        `Chegaram ${snapshots} snapshots em ${janela}, e nenhum trouxe o bloco `
        + 'de escalonamento. Os contadores existem no SDK; esta frota ainda roda '
        + 'uma build que não os envia. "Ninguém reporta" NÃO é "ninguém atrasou".',
      saude: 'ok',
      rotulo: 'sem dado',
      famintos: 0,
      atrasados: 0,
    };
  }

  const attributed = numeroOuIndefinido(f.snapshots_attributed);
  if (attributed === undefined) return veredictoDadosIncompletos('snapshots_attributed', janela);

  const sessionsReporting = numeroOuIndefinido(f.sessions_reporting);
  if (sessionsReporting === undefined) return veredictoDadosIncompletos('sessions_reporting', janela);

  // ------------------------------------------------------------
  // Totais NÃO atribuídos (registry inteiro, sem separar por detector).
  //
  // ⚠️ IRMÃO DO DEFEITO ACIMA: esta checagem tem de rodar sempre que existir
  // AO MENOS UM snapshot sem atribuição (`naoAtribuidos > 0`) — nunca só
  // dentro do ramo em que TODOS os snapshots são assim. Frota mista (rollout
  // em andamento, SDKs de versões diferentes) é o estado NORMAL: um único
  // snapshot com detalhamento limpo não pode apagar centenas de outros, com
  // atraso enorme e sem atribuição, do veredito.
  // ------------------------------------------------------------
  const naoAtribuidos = reporting - attributed;
  const un = f.unattributed;
  const unLateRuns = numeroOuIndefinido(un?.late_runs) ?? 0;
  const unSlowRuns = numeroOuIndefinido(un?.slow_runs) ?? 0;
  const unMaxLateness = numeroOuIndefinido(un?.max_lateness_ms) ?? 0;
  const unMaxRun = numeroOuIndefinido(un?.max_run_ms) ?? 0;
  const unCegueira = Math.max(unMaxLateness, unMaxRun);
  // Sem cadência conhecida pra um total agregado, só o piso absoluto de fome
  // se aplica (o critério de 10× exige a cadência de UM detector).
  const unHouveProblema = naoAtribuidos > 0 && (unLateRuns > 0 || unSlowRuns > 0);
  const unFaminto = unHouveProblema && unCegueira >= CEGUEIRA_ABSOLUTA_FAMINTO_MS;

  const linhas = ordenarDetectores(f.detectors ?? []);
  const famintosAtribuidos = linhas.filter((l) => l.saude === 'faminto').length;
  const atrasadosAtribuidos = linhas.filter((l) => l.saude === 'atrasado').length;
  // Um problema não-atribuído nunca é descartado — vira +1 no total, do jeito
  // que o pior dos casos (fome) já domina o atraso simples.
  const famintos = famintosAtribuidos + (unFaminto ? 1 : 0);
  const atrasados = atrasadosAtribuidos + (unHouveProblema && !unFaminto ? 1 : 0);

  // `attributed === 0` significa "NENHUM SDK desta janela sabe atribuir" — e
  // significa isso mesmo desde que o backend passou a ler o bit explícito do
  // SDK. Antes ele derivava de a lista de detectores estar vazia, e com formato
  // delta esse ramo teria engolido a frota 100% saudável.
  if (attributed === 0) {
    return {
      estado: 'sem_atribuicao',
      frase: unHouveProblema
        ? 'A frota atrasou, mas esta versão do SDK não diz de qual detector.'
        : 'A frota reporta escalonamento em dia, sem detalhe por detector.',
      detalhe: unHouveProblema
        ? `Pior atraso ${formatarDuracao(unMaxLateness)} e pior tick `
          + `${formatarDuracao(unMaxRun)} em ${janela}, somando `
          + `${unLateRuns} execuções atrasadas. Os contadores do SDK são `
          + 'do registry inteiro, sem separar por detector — por isso a tabela '
          + 'abaixo está vazia. Preferimos dizer isto a apontar um culpado que o '
          + 'dado não sustenta.'
        : `${reporting} snapshots reportaram, nenhum com atraso — mas `
          + 'sem detalhe por detector, então a tabela fica vazia.',
      saude: unFaminto ? 'faminto' : unHouveProblema ? 'atrasado' : 'ok',
      rotulo: rotuloDaSaude(unFaminto ? 'faminto' : unHouveProblema ? 'atrasado' : 'ok'),
      famintos,
      atrasados,
    };
  }

  // Pior linha ATRIBUÍDA da janela — só conta se ela de fato tiver problema
  // (a lista inclui detectores saudáveis também, ordenados por último).
  const piorAtribuido = linhas.length > 0 && linhas[0].saude !== 'ok' ? linhas[0] : undefined;
  const culpadoAtribuido = linhas.find((l) => l.papel === 'segurou_fila' || l.papel === 'ambos');

  if (famintos > 0) {
    const partes: string[] = [];
    if (piorAtribuido && piorAtribuido.saude === 'faminto') {
      partes.push(
        `Pior buraco de detecção ATRIBUÍDO: ${formatarDuracao(piorAtribuido.cegueiraMs)} em `
        + `${piorAtribuido.dado.detector_name}.`
        + (culpadoAtribuido
          ? ` A varredura é serial — quem segurou a thread foi ${culpadoAtribuido.dado.detector_name}, `
            + `com tick de ${formatarDuracao(culpadoAtribuido.dado.worst_run_ms)}.`
          : ' Nenhum tick longo foi atribuído nesta janela: o atraso veio de outro lugar '
            + '(máquina saturada, suspensão do processo) e não de um detector segurando a fila.'),
      );
    }
    if (unFaminto) {
      partes.push(
        `Além disso, ${naoAtribuidos} snapshot(s) sem detalhamento por detector também `
        + `mostram cegueira grave nesta janela (pior atraso ${formatarDuracao(unMaxLateness)}, `
        + `pior tick ${formatarDuracao(unMaxRun)}) — esse volume não é descartado só porque `
        + 'outro snapshot chegou com detalhamento limpo.',
      );
    }
    return {
      estado: 'degradada',
      frase: famintos === 1
        ? '1 detector passou fome nesta janela.'
        : `${famintos} detectores passaram fome nesta janela.`,
      detalhe: `${partes.join(' ')} Detector lento é problema nosso, não conduta de jogador.`,
      saude: 'faminto',
      rotulo: rotuloDaSaude('faminto'),
      famintos,
      atrasados,
    };
  }

  if (atrasados > 0) {
    const partes: string[] = [];
    if (atrasadosAtribuidos > 0) {
      partes.push(
        atrasadosAtribuidos === 1
          ? '1 detector atribuído saiu da cadência configurada, dentro da ordem de grandeza esperada.'
          : `${atrasadosAtribuidos} detectores atribuídos saíram da cadência configurada, `
            + 'dentro da ordem de grandeza esperada.',
      );
    }
    if (unHouveProblema && !unFaminto) {
      partes.push(
        `${naoAtribuidos} snapshot(s) sem detalhamento por detector também mostram atraso `
        + `(pior ${formatarDuracao(unCegueira)}), abaixo do piso que classificaria como fome.`,
      );
    }
    return {
      estado: 'degradada',
      frase: atrasados === 1
        ? '1 detector saiu da cadência configurada, dentro da ordem de grandeza esperada.'
        : `${atrasados} detectores saíram da cadência configurada, dentro da ordem de grandeza esperada.`,
      detalhe: `${partes.join(' ')} Nenhum passou de 30 s sem rodar. ${RESSALVA_CRITERIO_DE_RAZAO} `
        + 'É desvio, não cegueira — vale acompanhar, não vale acordar ninguém.',
      saude: 'atrasado',
      rotulo: rotuloDaSaude('atrasado'),
      famintos,
      atrasados,
    };
  }

  // Chegar aqui com `linhas` VAZIA é o caso normal do formato delta: os SDKs
  // sabem atribuir (`attributed > 0`) e não tiveram ninguém a atribuir. Não é
  // ausência de dado — é a resposta, e é positiva.
  return {
    estado: 'saudavel',
    frase: 'Nenhum detector saiu da cadência nesta janela.',
    detalhe:
      `${sessionsReporting} sessões reportaram escalonamento em ${janela}. `
      + 'Todos os detectores rodaram dentro da tolerância'
      + (naoAtribuidos > 0
        ? `, incluindo os ${naoAtribuidos} snapshot(s) sem detalhamento por detector `
          + '(totais do registry também sem atraso).'
        : '.'),
    saude: 'ok',
    rotulo: rotuloDaSaude('ok'),
    famintos: 0,
    atrasados: 0,
  };
}
